import { useState, useEffect, useRef, useCallback } from "react";
import SessionContext from "./SessionContext";
import Session from "./Session";
import { CommonCalls as Calls } from "../../../config/calls";
import FetchClient from "../../tools/FetchClient";

// global object because we need to keep only one session alive
const session = new Session();

// TODO think of a way to get this from backend during some startup or something,
// or at least a way to modify this in runtime
const REFRESH_TOKEN_INTERVAL = 1000 * 60 * 5;

// these are all caused by invalid user input, so we handle it differently
const VALID_ERROR_CODES = [
  "invalidRefreshToken",
  "loginFailed",
  "registrationFailed",
  "refreshTokenMismatch",
  "missingCsrfToken",
  "invalidCsrfToken",
  "userNotVerified",
];

const BROADCAST_CHANNEL = new BroadcastChannel("SESSION_BROADCAST");
const CSRF_TOKEN_KEY = "session_csrf_token";

function shouldBeError(err) {
  const errCode = err.code?.split("/").pop();
  return !VALID_ERROR_CODES.includes(errCode);
}

function SessionContextProvider({ children }) {
  const [call, setCall] = useState({ feedback: "silent" });
  // Track authentication state in React state so effects can properly respond to changes
  const [isAuthenticated, setIsAuthenticated] = useState(session.authenticated);
  // Use ref to prevent double calls in React strict mode (dev mode double mounting)
  const initialRequestDoneRef = useRef(false);
  const intervalRef = useRef(null);

  /**
   * Updates session state and syncs to React state
   * @param {boolean} authenticated - Whether the session is authenticated
   * @param {object} sessionData - Session data to store
   */
  const updateSessionState = useCallback((authenticated, sessionData) => {
    if (authenticated) {
      session.authenticate(sessionData.token, sessionData.user);
    } else {
      session.logout();
    }
    setIsAuthenticated(session.authenticated);
    FetchClient.setSession(authenticated ? sessionData : null);
  }, []);

  /**
   * Wraps API calls with session management and error handling
   * @param {Function} callFn - The API call function to execute
   * @param {object} opts - Options for the call (isLogout flag)
   * @returns {Promise} Promise that resolves/rejects with call result
   */
  const _wrapCall = useCallback(
    (callFn, opts = { isLogout: false }) => {
      return callFn()
        .then((response) => {
          const authenticated = !opts.isLogout;
          updateSessionState(authenticated, response.data);

          // store Csrf token in local storage -> can get undefined in logout, which is correct
          localStorage.setItem(CSRF_TOKEN_KEY, response.headers["x-csrf-token"] || "");

          BROADCAST_CHANNEL.postMessage({ isLogout: opts.isLogout, data: response.data });

          const newCall = { feedback: "ready", data: response.data, response };
          setCall(newCall);
          return newCall;
        })
        .catch((err) => {
          updateSessionState(false, null);

          const errorData = err.response?.data || err;
          const feedback = shouldBeError(errorData) ? "error" : "ready";
          const newCall = { feedback: feedback, data: errorData, response: err };
          setCall(newCall);

          if (feedback === "error") {
            throw err;
          } else {
            return err;
          }
        });
    },
    [updateSessionState],
  );

  /**
   * Registers a new user account
   * @param {object} body - Registration data
   * @returns {Promise} Promise that resolves/rejects with registration result
   */
  const register = useCallback(
    (body) => {
      return _wrapCall(() => Calls.register(body, { withCredentials: true }));
    },
    [_wrapCall],
  );

  /**
   * Logs in a user with username and password
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise} Promise that resolves/rejects with login result
   */
  const login = useCallback(
    (username, password) => {
      return _wrapCall(() => Calls.login({ username, password }, { withCredentials: true }));
    },
    [_wrapCall],
  );

  /**
   * Logs out the current user
   * @returns {Promise} Promise that resolves/rejects with logout result
   */
  const logout = useCallback(() => {
    return _wrapCall(() => Calls.logout(null, { withCredentials: true }), { isLogout: true });
  }, [_wrapCall]);

  /**
   * Refreshes the authentication token
   * @returns {Promise} Promise that resolves/rejects with refresh result
   */
  const refreshToken = useCallback(() => {
    const csrfToken = localStorage.getItem(CSRF_TOKEN_KEY) || undefined;
    return _wrapCall(() =>
      Calls.refreshToken(null, {
        withCredentials: true,
        headers: { "X-Csrf-Token": csrfToken },
      }),
    );
  }, [_wrapCall]);

  // Handle broadcast messages from other tabs/windows
  useEffect(() => {
    BROADCAST_CHANNEL.onmessage = (message) => {
      const { isLogout, data } = message.data;
      updateSessionState(!isLogout, data);
      setCall({ feedback: "ready", data: data, response: null });
    };
  }, [updateSessionState]);

  // Call refreshToken on initial mount (only once, even in React strict mode)
  useEffect(() => {
    if (!initialRequestDoneRef.current) {
      initialRequestDoneRef.current = true;
      refreshToken();
    }
  }, [refreshToken]);

  // Register automatic token refresh when authenticated
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Set up interval if authenticated
    if (isAuthenticated) {
      intervalRef.current = setInterval(() => {
        refreshToken().catch(() => {
          // Silently handle refresh errors - they're already handled in _wrapCall
        });
      }, REFRESH_TOKEN_INTERVAL);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, refreshToken]);

  const sessionValue = {
    session,
    login,
    logout,
    register,
    call,
  };

  return (
    <SessionContext.Provider value={sessionValue}>
      {typeof children === "function" ? children(sessionValue) : children}
    </SessionContext.Provider>
  );
}

export default SessionContextProvider;
